import axios from "axios";

import { getIdToken, signOut } from "../auth/auth";
import { isMSquaredHub } from "../utils/isEnv";
import { getRequestOrigin } from "../utils/requestOrigin";
import { getWorkspaceFromUrl } from "../workspace/utils/getWorkspaceFromUrl";
import { getActiveWorkspaceId } from "../workspace/utils/workspaceStorage";

export const publicAxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  timeout: 0,
  headers: {
    "Content-Type": "application/json",
  },
});

// Create axios instance with base configuration
// In development, don't set baseURL so MSW can intercept requests properly
export const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  timeout: 0,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

axiosInstance.interceptors.request.use(
  async (config) => {
    if (
      config.url &&
      isMSquaredHub() &&
      config.url.includes("/api/auth/token")
    ) {
      return config;
    }

    const jwt = await getIdToken();
    if (!jwt) {
      console.error("No JWT token available, signing out");
      await signOut();
      return new Promise(() => {});
    }

    config.headers.set("token", jwt);
    config.headers.set("app-type", "app");
    config.headers.set("x-request-origin", getRequestOrigin());

    // For msquared hub, set workspace subdomain header
    if (isMSquaredHub()) {
      const workspaceSubdomain = getWorkspaceFromUrl();
      if (workspaceSubdomain) {
        config.headers.set("x-workspace-subdomain", workspaceSubdomain);
      }
    } else {
      const workspaceId = getActiveWorkspaceId();
      if (workspaceId) {
        config.headers.set("x-workspace-id", workspaceId);
      }
    }

    return config;
  },
  (error) => {
    console.error("Request error:", error);
    return Promise.reject(error);
  },
);

axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    console.error("Response error:", error);

    if (
      error.response &&
      error.response.status === 403 &&
      error.response.headers["x-response-origin"] === "backend_express"
    ) {
      // Initialize retry count
      error.config._retryCount = error.config._retryCount || 0;
      error.config._retryCount += 1;

      // If we've already tried twice, give up
      if (error.config._retryCount > 2) {
        console.error(
          "Got 403, tried to get new JWT token 2 times already, signing out",
        );
        await signOut();
        return Promise.reject(error);
      }

      try {
        console.log("Got 403, trying to get new JWT token");
        const jwt = await getIdToken();

        if (!jwt) {
          throw new Error("No JWT token after refresh");
        }

        error.config.headers = error.config.headers || {};
        error.config.headers.token = jwt;
        error.config.headers["app-type"] = "app";
        error.config.headers["x-request-origin"] = getRequestOrigin();

        return axiosInstance.request(error.config);
      } catch (refreshError) {
        console.error("Failed to refresh token, signing out:", refreshError);
        await signOut();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);
